import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import React, {
  Fragment,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import cancelStyle from 'src/components/CancelSubscription/CancelSubscription.style'
import RadioButton from 'src/components/RadioButton/RadioButton'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CHURN_REASONS_WITH_FREE_TEXT,
  ChurnSurveyReason,
} from 'src/models/organizations.models'

import surveyStyle from './ChurnSurveyModal.style'

export interface ChurnSurveyScreenProps {
  onSubmit: (
    reason?: ChurnSurveyReason,
    comment?: string,
    additionalComment?: string,
  ) => void
  onSkip: () => void
}

const CHURN_REASONS: { value: ChurnSurveyReason; label: string }[] = [
  {
    value: ChurnSurveyReason.TOO_EXPENSIVE,
    label: 'Too expensive / not enough value for the price',
  },
  {
    value: ChurnSurveyReason.MISSING_FEATURES,
    label: 'Missing features I need',
  },
  {
    value: ChurnSurveyReason.SWITCHED_TO_COMPETITOR,
    label: 'Switched to another solution or bank',
  },
  {
    value: ChurnSurveyReason.NO_LONGER_NEEDED,
    label: 'No longer need the product',
  },
  {
    value: ChurnSurveyReason.TOO_DIFFICULT_TO_USE,
    label: 'Too difficult to use',
  },
]

export default function ChurnSurveyScreen(
  props: ChurnSurveyScreenProps,
): ReactElement {
  const { onSubmit, onSkip } = props
  const translate = useTranslate()
  const listImage = useColorOrGrayscaleImage(ImageNames.List)

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  )
  const [inlineComment, setInlineComment] = useState('')
  const [additionalComment, setAdditionalComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => clearTimeout(submitTimeoutRef.current)
  }, [])

  const selectedReason =
    selectedIndex !== undefined ? CHURN_REASONS[selectedIndex] : undefined

  const hasAdditionalComment = additionalComment.trim().length > 0
  const canSubmit = selectedReason !== undefined || hasAdditionalComment

  const handleSelectReason = (index: number): void => {
    if (index !== selectedIndex) {
      setInlineComment('')
      setSelectedIndex(index)
    }
  }

  const handleSubmit = (): void => {
    if (submitted || !canSubmit) return
    const showsInlineComment =
      selectedReason !== undefined &&
      CHURN_REASONS_WITH_FREE_TEXT.includes(selectedReason.value)
    // When the user submits general feedback without picking a reason,
    // silently tag the response as OTHER so the backend has a concrete reason.
    const effectiveReason = selectedReason?.value ?? ChurnSurveyReason.OTHER
    onSubmit(
      effectiveReason,
      showsInlineComment && inlineComment ? inlineComment : undefined,
      additionalComment || undefined,
    )
    setSubmitted(true)
    submitTimeoutRef.current = setTimeout(() => {
      onSkip()
    }, 1200)
  }

  return (
    <div css={[cancelStyle, surveyStyle]}>
      <img
        src={listImage}
        alt="list"
        className="CancelSubscription-list-image"
      />
      <div
        className="CancelSubscription-container"
        data-testid="churn-survey-modal"
      >
        <div className="CancelSubscription-text">
          <div
            className="CancelSubscription-header"
            data-testid="churn-survey-header"
          >
            {translate('Before you go...')}
          </div>
          <div
            className="CancelSubscription-subtext"
            data-testid="churn-survey-subtitle"
          >
            {translate(
              "We'd love to know why you're cancelling. Your feedback helps us improve.",
            )}
          </div>
        </div>

        <div
          className="ChurnSurveyModal-reasons"
          role="radiogroup"
          aria-label={translate('Reason for cancelling')}
        >
          {CHURN_REASONS.map((reason, index) => {
            const isSelected = index === selectedIndex
            const showsInlineField =
              isSelected && CHURN_REASONS_WITH_FREE_TEXT.includes(reason.value)
            const label = translate(reason.label)
            return (
              <Fragment key={reason.value}>
                <div
                  className={cn('ChurnSurveyModal-option', {
                    'is-selected': isSelected,
                  })}
                  data-testid={`churn-survey-reason-${reason.value}`}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={label}
                  tabIndex={0}
                  onClick={() => handleSelectReason(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSelectReason(index)
                    }
                  }}
                >
                  <RadioButton isSelected={isSelected} aria-label={label} />
                  <span className="ChurnSurveyModal-option-label">{label}</span>
                </div>
                {showsInlineField && (
                  <div className="ChurnSurveyModal-inline-free-text">
                    <TextAreaInput
                      label={translate('Please tell us more')}
                      placeholder={
                        reason.value ===
                        ChurnSurveyReason.SWITCHED_TO_COMPETITOR
                          ? translate('Who did you switch to?')
                          : reason.value === ChurnSurveyReason.MISSING_FEATURES
                            ? translate('What’s missing?')
                            : undefined
                      }
                      value={inlineComment}
                      onChange={(e) => setInlineComment(e.target.value)}
                      autoResize
                      testId="churn-survey-free-text"
                    />
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>

        <div className="ChurnSurveyModal-additional-comment">
          <TextAreaInput
            label={translate('Any additional comments?')}
            placeholder={translate('Please tell us more...')}
            value={additionalComment}
            onChange={(e) => setAdditionalComment(e.target.value)}
            autoResize
            testId="churn-survey-comment"
          />
        </div>

        <div className="CancelSubscription-button-container CancelSubscription-button-container--spread">
          <Button
            variant="no-border"
            onClick={onSkip}
            data-testid="churn-survey-skip"
          >
            {translate('Skip')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitted || !canSubmit}
            data-testid="churn-survey-submit"
          >
            {submitted ? (
              <>
                <CheckmarkIcon
                  fill="currentColor"
                  width={16}
                  height={16}
                  aria-hidden="true"
                />{' '}
                {translate('Submitted')}
              </>
            ) : (
              translate('Submit Feedback')
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
